<script setup>
import { ref, onMounted } from "vue";
import { useAlert } from "dashboard/composables";
import { useAdmin } from "dashboard/composables/useAdmin";
import SettingsLayout from "../SettingsLayout.vue";
import BaseSettingsHeader from "../components/BaseSettingsHeader.vue";
import Button from "dashboard/components-next/button/Button.vue";
import aiRoutingConfigApi from "../../../../api/aiRoutingConfig";
import omniraApi from "../../../../api/omniraApi";
import AgentsAPI from "../../../../api/agents";

const { isAdmin } = useAdmin();
const loading = ref(false);
const saving = ref(false);
const agents = ref([]);

const form = ref({
  ai_endpoint: "",
  ai_api_key: "",
  ai_model: "",
  agent_caps: {},
  label_suggestions_enabled: false,
});
const apiKeySet = ref(false);
const endpointSet = ref(false);

const kbDocs = ref([]);
const kbForm = ref({ title: "", content: "" });
const kbAdding = ref(false);
const kbFile = ref(null);
const kbUploading = ref(false);
const kbEditing = ref(null);
const kbSaving = ref(false);

const rules = ref([]);
const ruleForm = ref({ name: "", instruction: "" });
const ruleAdding = ref(false);

const logs = ref([]);
const logsLoading = ref(false);
const pendingLabels = ref([]);
const pendingLoading = ref(false);

onMounted(async () => {
  loading.value = true;
  try {
    const [configRes, agentsRes, kbRes, rulesRes, logsRes, pendingRes] = await Promise.all([
      aiRoutingConfigApi.get(),
      AgentsAPI.get(),
      omniraApi.getKbDocuments(),
      omniraApi.getRules(),
      omniraApi.getLogs(),
      omniraApi.getActivityLogs(),
      omniraApi.getPendingLabels().catch(() => ({ data: [] })),
    ]);
    const data = configRes.data;
    
    form.value.ai_model = data.ai_model || "";
    form.value.agent_caps = data.agent_caps || {};
    form.value.label_suggestions_enabled = !!data.label_suggestions_enabled;
    apiKeySet.value = data.ai_api_key_set;
    endpointSet.value = !!data.ai_endpoint_set;
    const agentList =
      agentsRes.data?.payload || agentsRes.payload || agentsRes.data || [];
    agents.value = Array.isArray(agentList) ? agentList : [];
    kbDocs.value = kbRes.data || [];
    rules.value = rulesRes.data || [];
    logs.value = logsRes.data || [];
    pendingLabels.value = (pendingRes && pendingRes.data) || [];
  } catch (e) {
    useAlert("Failed to load AI config");
  } finally {
    loading.value = false;
  }
});

const refreshPendingLabels = async () => {
  pendingLoading.value = true;
  try {
    const res = await omniraApi.getPendingLabels();
    pendingLabels.value = (res && res.data) || [];
  } catch { pendingLabels.value = []; }
  finally { pendingLoading.value = false; }
};
const approvePending = async (id) => {
  try { await omniraApi.approvePendingLabel(id); useAlert("Label approved"); await refreshPendingLabels(); }
  catch (e) { useAlert("Approve failed"); }
};
const rejectPending = async (id) => {
  try { await omniraApi.rejectPendingLabel(id); useAlert("Label rejected"); await refreshPendingLabels(); }
  catch (e) { useAlert("Reject failed"); }
};
const save = async () => {
  saving.value = true;
  try {
    const payload = { ...form.value };
    if (!payload.ai_api_key) delete payload.ai_api_key;
    await aiRoutingConfigApi.update(payload);
    useAlert("AI settings saved successfully");
    apiKeySet.value = !!payload.ai_api_key || apiKeySet.value;
    form.value.ai_api_key = "";
    if (payload.ai_endpoint) { endpointSet.value = true; form.value.ai_endpoint = ""; }
  } catch (e) {
    useAlert("Failed to save settings: " + (e.response?.data?.error || e.message));
  } finally {
    saving.value = false;
  }
};


const editKbDoc = doc => {
  kbEditing.value = { ...doc };
};

const cancelKbEdit = () => {
  kbEditing.value = null;
};

const saveKbDoc = async () => {
  if (!kbEditing.value || !kbEditing.value.title || !kbEditing.value.content) return;
  kbSaving.value = true;
  try {
    const res = await omniraApi.updateKbDocument(kbEditing.value.id, {
      title: kbEditing.value.title,
      content: kbEditing.value.content,
    });
    const idx = kbDocs.value.findIndex(d => d.id === kbEditing.value.id);
    if (idx !== -1) kbDocs.value[idx] = res.data;
    kbEditing.value = null;
    useAlert("Document updated");
  } catch (e) {
    useAlert("Failed to update document: " + (e.response?.data?.error || e.message));
  } finally {
    kbSaving.value = false;
  }
};

const addKbDoc = async () => {
  if (!kbForm.value.title || !kbForm.value.content) return;
  kbAdding.value = true;
  try {
    const res = await omniraApi.createKbDocument(kbForm.value);
    kbDocs.value.push(res.data);
    kbForm.value = { title: "", content: "" };
    useAlert("Knowledge base document added");
  } catch (e) {
    useAlert("Failed to add document: " + (e.response?.data?.error || e.message));
  } finally {
    kbAdding.value = false;
  }
};

const uploadKbFile = async () => {
  if (!kbFile.value) return;
  kbUploading.value = true;
  try {
    const res = await omniraApi.uploadKbDocument(kbFile.value);
    kbDocs.value.push(res.data);
    kbFile.value = null;
    useAlert("PDF uploaded to knowledge base");
  } catch (e) {
    useAlert("Failed to upload file: " + (e.response?.data?.error || e.message));
  } finally {
    kbUploading.value = false;
  }
};
const onKbFileChange = e => {
  kbFile.value = e.target.files && e.target.files[0] ? e.target.files[0] : null;
};
const deleteKbDoc = async id => {
  try {
    await omniraApi.deleteKbDocument(id);
    kbDocs.value = kbDocs.value.filter(d => d.id !== id);
  } catch (e) {
    useAlert("Failed to delete document");
  }
};

const addRule = async () => {
  if (!ruleForm.value.name || !ruleForm.value.instruction) return;
  ruleAdding.value = true;
  try {
    const res = await omniraApi.createRule(ruleForm.value);
    rules.value.push(res.data);
    ruleForm.value = { name: "", instruction: "" };
    useAlert("Rule added");
  } catch (e) {
    useAlert("Failed to add rule: " + (e.response?.data?.error || e.message));
  } finally {
    ruleAdding.value = false;
  }
};

const toggleRule = async rule => {
  try {
    const res = await omniraApi.updateRule(rule.id, { active: !rule.active });
    rule.active = res.data.active;
  } catch (e) {
    useAlert("Failed to update rule");
  }
};

const deleteRule = async id => {
  try {
    await omniraApi.deleteRule(id);
    rules.value = rules.value.filter(r => r.id !== id);
  } catch (e) {
    useAlert("Failed to delete rule");
  }
};

const refreshLogs = async () => {
  logsLoading.value = true;
  try {
    const res = await omniraApi.getLogs();
    logs.value = res.data || [];
  } catch (e) {
    useAlert("Failed to load logs");
  } finally {
    logsLoading.value = false;
  }
};

const fmtTime = t => new Date(t).toLocaleString();
</script>

<template>
  <SettingsLayout>
    <BaseSettingsHeader
      title="AI"
      description="Configure the AI assistant that replies to customers in bot-handled conversations"
      icon="sparkle"
    />
    <div v-if="loading" class="p-8 text-center text-slate-500">Loading...</div>
    <div v-else class="space-y-8 p-6 max-w-3xl">
      <section class="space-y-4">
        <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100">AI Provider</h3>
        <div class="space-y-3">
          <div>
            <label class="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">Base URL</label>
            <input
              v-model="form.ai_endpoint"
              :type="endpointSet ? 'password' : 'text'"
              placeholder="https://api.example.com"
              class="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">API Key</label>
            <input
              v-model="form.ai_api_key"
              type="password"
              :placeholder="apiKeySet ? 'Key is set - enter new value to change' : 'Enter API key'"
              class="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">Model</label>
            <input
              v-model="form.ai_model"
              type="text"
              placeholder="claude-sonnet-5"
              class="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
            />
          </div>
        </div>
      </section>

      <section class="space-y-4">
        <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100">Agent Workload Cap</h3>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          Maximum number of open conversations an agent can hold. An agent at their cap receives no new
          assignments until one of their conversations is resolved. Leave empty for unlimited.
        </p>
        <div class="space-y-2">
          <div
            v-for="agent in agents"
            :key="agent.id"
            class="flex items-center justify-between gap-4 py-1"
          >
            <span class="text-sm text-slate-700 dark:text-slate-200">{{ agent.name }}</span>
            <input
              v-model="form.agent_caps[agent.id]"
              type="number"
              min="1"
              placeholder="unlimited"
              class="w-28 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
            />
          </div>
        </div>
      </section>

      <section class="space-y-4">
        <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100">Knowledge Base</h3>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          Documents the AI uses as its knowledge base when answering customers.
        </p>
        <div class="space-y-2">
          <div
            v-for="doc in kbDocs"
            :key="doc.id"
            class="rounded-lg border border-slate-200 dark:border-slate-700 p-3"
          >
            <template v-if="kbEditing && kbEditing.id === doc.id">
              <input
                v-model="kbEditing.title"
                type="text"
                class="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 mb-2 text-sm text-slate-800 dark:text-slate-100"
              />
              <textarea
                v-model="kbEditing.content"
                rows="6"
                class="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 mb-2 text-sm text-slate-800 dark:text-slate-100"
              />
              <div class="flex gap-2">
                <Button label="Save" size="sm" :loading="kbSaving" :disabled="!isAdmin" @click="saveKbDoc" />
                <Button label="Cancel" size="sm" variant="faded" color="slate" @click="cancelKbEdit" />
              </div>
            </template>
            <template v-else>
              <div class="flex items-center justify-between gap-4">
                <span class="text-sm font-medium text-slate-700 dark:text-slate-200">{{ doc.title }}</span>
                <div v-if="!doc.title.includes('File Upload Guide')" class="flex items-center gap-3 shrink-0">
                  <button class="text-xs text-slate-600 hover:text-slate-800 dark:text-slate-300" @click="editKbDoc(doc)">
                    Edit
                  </button>
                  <button class="text-xs text-red-600 hover:text-red-700" @click="deleteKbDoc(doc.id)">
                    Delete
                  </button>
                </div>
              </div>
              <p class="mt-1 text-xs text-slate-500 dark:text-slate-400 whitespace-pre-wrap">{{ doc.content }}</p>
            </template>
          </div>
          <p v-if="kbDocs.length === 0" class="text-xs text-slate-400">No documents yet.</p>
        </div>
        <div class="space-y-3 border-t border-slate-200 dark:border-slate-700 pt-3">
              <div>
                <label class="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">Upload PDF</label>
                <p class="text-xs text-slate-400 mb-2">PDF only, max 10 MB. The AI extracts text and learns from it.</p>
                <div class="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".pdf"
                    class="text-sm text-slate-700 dark:text-slate-200 file:mr-3 file:rounded-lg file:border-0 file:bg-woot-500 file:px-3 file:py-1.5 file:text-sm file:text-white hover:file:bg-woot-600"
                    @change="onKbFileChange"
                  />
                  <Button label="Upload PDF" size="sm" :loading="kbUploading" :disabled="!isAdmin || !kbFile" @click="uploadKbFile" />
                </div>
              </div>
              <div class="space-y-2">
                <p class="text-xs font-medium text-slate-500 dark:text-slate-400">Or add as plain text</p>
            <input
              v-model="kbForm.title"
            type="text"
            placeholder="Document title"
            class="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100"
          />
          <textarea
            v-model="kbForm.content"
            rows="4"
            placeholder="Paste the document content here"
            class="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100"
          />
          <Button label="Add document" size="sm" :loading="kbAdding" :disabled="!isAdmin" @click="addKbDoc" />
        </div>
        </div>
      </section>

      <section class="space-y-4">
        <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100">AI Label Suggestions</h3>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          When enabled, the AI analyzes each conversation and suggests relevant labels/tags based on its content.
        </p>
        <label class="flex items-center gap-3 cursor-pointer">
          <input
            v-model="form.label_suggestions_enabled"
            type="checkbox"
            class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-500 focus:ring-woot-500"
          />
          <span class="text-sm text-slate-700 dark:text-slate-200">Enable AI label suggestions</span>
        </label>
      </section>
      <section class="space-y-4">
        <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100">Custom Rules</h3>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          Behaviour instructions applied on top of the AI base prompt.
        </p>
        <div class="space-y-2">
          <div
            v-for="rule in rules"
            :key="rule.id"
            class="flex items-center justify-between gap-4 py-1"
          >
            <div class="min-w-0">
              <span class="text-sm text-slate-700 dark:text-slate-200">{{ rule.name }}</span>
              <p class="text-xs text-slate-400 truncate">{{ rule.instruction }}</p>
            </div>
            <div class="flex items-center gap-3 shrink-0">
              <button
                class="text-xs"
                :class="rule.active ? 'text-n-teal-9' : 'text-slate-400'"
                @click="toggleRule(rule)"
              >
                {{ rule.active ? "Active" : "Inactive" }}
              </button>
              <button class="text-xs text-red-600 hover:text-red-700" @click="deleteRule(rule.id)">
                Delete
              </button>
            </div>
          </div>
          <p v-if="rules.length === 0" class="text-xs text-slate-400">No rules yet.</p>
        </div>
        <div class="space-y-2 border-t border-slate-200 dark:border-slate-700 pt-3">
          <input
            v-model="ruleForm.name"
            type="text"
            placeholder="Rule name"
            class="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100"
          />
          <input
            v-model="ruleForm.instruction"
            type="text"
            placeholder="Instruction, e.g. Always reply in short sentences"
            class="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100"
          />
          <Button label="Add rule" size="sm" :loading="ruleAdding" :disabled="!isAdmin" @click="addRule" />
        </div>
      </section>

      <section class="space-y-4">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100">AI Logs</h3>
          <Button label="Refresh" size="sm" variant="faded" color="slate" :loading="logsLoading" @click="refreshLogs" />
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          Last {{ logs.length }} AI calls, newest first.
        </p>
        <div class="space-y-2">
          <div
            v-for="log in logs"
            :key="log.id"
            class="rounded-lg border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-600 dark:text-slate-300"
          >
            <div class="flex items-center justify-between gap-2">
              <span class="font-medium text-slate-700 dark:text-slate-200">{{ log.decision || log.status }}</span>
              <span>{{ fmtTime(log.created_at) }}</span>
            </div>
            <p class="mt-1">model: {{ log.model }} - {{ log.latency_ms ? log.latency_ms + "ms" : "—" }} - conversation #{{ log.conversation_id }}</p>
          </div>
          <p v-if="logs.length === 0" class="text-xs text-slate-400">No AI activity yet.</p>
        </div>
      </section>

      

      <div class="pt-2">
        <Button
          label="Save changes"
          :loading="saving"
          :disabled="!isAdmin"
          @click="save"
        />
      </div>
    </div>
  </SettingsLayout>
</template>